import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiRepeat, FiX } from "react-icons/fi";
import { createApiClient } from "../lib/apiClient";
import { operationalToday } from "../lib/dayAccess";
import { notifySuccess } from "../lib/notifications";
import { addDays, displayDate, displayWeekdays, scheduledDates, WEEKDAYS } from "../lib/activitySchedule";
import "./ScheduledActivities.css";

const api = createApiClient();
const endpoint = "/api/operaciones/actividades-programadas";
const message = (error) => error?.response?.data?.error || "No se pudo guardar el cambio. Volvé a intentarlo.";
const nonce = () => window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export function ScheduledActivityMark() {
  return <FiRepeat className="scheduled-activity-mark" aria-hidden="true"
    title="Actividad programada. Se completa de forma independiente en cada fecha." />;
}

export default function ScheduledActivities({ deposito, selectedDate, onChanged }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("new");
  const [form, setForm] = useState({});
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submitting = useRef(false);
  const request = useRef(null);
  const dialog = useRef(null);
  const trigger = useRef(null);
  const generation = useRef(0);
  const scope = `?deposito=${encodeURIComponent(deposito)}`;
  const dates = scheduledDates(form.desde, form.hasta, form.diasSemana || []);

  useEffect(() => { generation.current += 1; setOpen(false); }, [deposito]);
  useEffect(() => {
    if (!open) return undefined;
    const originalOverflow = document.body.style.overflow;
    const triggerButton = trigger.current;
    document.body.style.overflow = "hidden";
    dialog.current?.querySelector("button")?.focus();
    return () => { document.body.style.overflow = originalOverflow; triggerButton?.focus(); };
  }, [open]);

  async function loadList() {
    const version = ++generation.current;
    setLoading(true);
    try {
      const { data } = await api.get(endpoint + scope);
      if (version === generation.current) setList(Array.isArray(data) ? data : []);
    } catch (err) { if (version === generation.current) setError(err?.response?.data?.error || "No se pudieron cargar las programaciones. Volvé a intentarlo."); }
    finally { if (version === generation.current) setLoading(false); }
  }

  function show() {
    const today = operationalToday();
    const desde = selectedDate > today ? selectedDate : today;
    setForm({ observacion: "", ambito: "fiscal", desde, hasta: addDays(desde, 30), diasSemana: [1] });
    request.current = null;
    setError(""); setTab("new"); setOpen(true); loadList();
  }

  async function mutate(action, success) {
    if (submitting.current) return;
    submitting.current = true; setBusy(true); setError("");
    try {
      await action();
      notifySuccess(success);
      setTab("list");
      await loadList();
      await onChanged?.();
    } catch (err) { setError(message(err)); }
    finally { submitting.current = false; setBusy(false); }
  }

  function save(event) {
    event.preventDefault();
    if (!form.observacion.trim() || !dates.length || form.desde < operationalToday()) {
      setError("Completá la actividad y elegí un período desde hoy que incluya los días seleccionados."); return;
    }
    const payload = { ...form, observacion: form.observacion.trim(), diasSemana: [...form.diasSemana].sort((a, b) => a - b) };
    const signature = JSON.stringify(payload);
    if (request.current?.signature !== signature) request.current = { signature, id: nonce() };
    mutate(() => api.post(endpoint + scope, { ...payload, solicitudId: request.current.id }),
      `Actividad programada en ${dates.length} fecha${dates.length === 1 ? "" : "s"}.`);
  }

  function cancel(item) {
    if (!window.confirm("¿Cancelar las repeticiones pendientes desde hoy? Se conservarán las anteriores y las que ya estén completadas.")) return;
    mutate(() => api.post(`${endpoint}/${item._id}/cancelar${scope}`, {}), "Repeticiones pendientes canceladas.");
  }

  function keyboard(event) {
    if (event.key === "Escape" && !submitting.current) { event.stopPropagation(); setOpen(false); }
    if (event.key !== "Tab") return;
    const elements = [...dialog.current.querySelectorAll("button:not(:disabled), input:not(:disabled), textarea:not(:disabled), select:not(:disabled)")];
    const first = elements[0], last = elements[elements.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
  }

  return <>
    <button type="button" className="btn ghost" ref={trigger} onClick={show}>
      <FiRepeat aria-hidden="true" /> Programar actividad
    </button>
    {open && createPortal(<div className="activity-schedule-backdrop" onMouseDown={(e) => {
      if (e.target === e.currentTarget && !submitting.current) setOpen(false);
    }}>
      <section className="activity-schedule" role="dialog" aria-modal="true" aria-labelledby="activity-schedule-title"
        ref={dialog} onKeyDown={keyboard}>
        <header className="activity-schedule-header">
          <h2 id="activity-schedule-title"><FiRepeat aria-hidden="true" /> Actividades programadas</h2>
          <button type="button" aria-label="Cerrar programaciones" disabled={busy} onClick={() => setOpen(false)}><FiX /></button>
        </header>
        <div className="activity-schedule-tabs" role="tablist" aria-label="Programaciones">
          <button type="button" role="tab" aria-selected={tab === "new"} disabled={busy}
            onClick={() => { setTab("new"); setError(""); }}>Nueva actividad</button>
          <button type="button" role="tab" aria-selected={tab === "list"} disabled={busy}
            onClick={() => { setTab("list"); setError(""); loadList(); }}>Programaciones existentes</button>
        </div>
        {error && <p className="activity-schedule-error" role="alert">{error}</p>}
        {tab === "new" ? <form onSubmit={save} className="activity-schedule-form" aria-label="Nueva actividad programada">
          <p>La actividad aparecerá en Observaciones/Anotaciones de cada fecha elegida. Cada repetición se completa por separado.</p>
          <label>Actividad<textarea required maxLength={4000} rows={3} value={form.observacion}
            onChange={(e) => setForm({ ...form, observacion: e.target.value })} disabled={busy} /></label>
          <div className="activity-schedule-fields">
            <label>Ámbito<select value={form.ambito} disabled={busy} onChange={(e) => setForm({ ...form, ambito: e.target.value })}>
              <option value="fiscal">Fiscal</option><option value="nacional">Nacional</option>
            </select></label>
            <label>Fecha de inicio<input type="date" required min={operationalToday()} value={form.desde} disabled={busy}
              onChange={(e) => setForm({ ...form, desde: e.target.value })} /></label>
            <label>Hasta (inclusive)<input type="date" required min={form.desde} max={form.desde ? addDays(form.desde, 366 * 5) : undefined}
              value={form.hasta} disabled={busy} onChange={(e) => setForm({ ...form, hasta: e.target.value })} /></label>
          </div>
          <fieldset disabled={busy}><legend>Repetir los días</legend><div className="activity-schedule-weekdays">
            {WEEKDAYS.map(([day, label]) => <label key={day} className={form.diasSemana.includes(day) ? "selected" : ""}>
              <input type="checkbox" checked={form.diasSemana.includes(day)} onChange={(e) => setForm({ ...form,
                diasSemana: e.target.checked ? [...form.diasSemana, day] : form.diasSemana.filter((value) => value !== day) })} />{label}
            </label>)}
          </div></fieldset>
          <p className="activity-schedule-preview" role="status">{dates.length
            ? `${dates.length} repeticiones · Primera: ${displayDate(dates[0])} · Última: ${displayDate(dates[dates.length - 1])}`
            : "Elegí fechas y días de la semana que coincidan. El período máximo es de cinco años."}</p>
          <footer><button type="button" disabled={busy} onClick={() => setOpen(false)}>Cerrar</button>
            <button type="submit" className="primary" disabled={busy || !dates.length || !form.observacion.trim()}>
              {busy ? "Guardando…" : "Guardar programación"}</button></footer>
        </form> : <div className="activity-schedule-list" aria-busy={loading}>
          <p>Cancelar elimina solamente las repeticiones pendientes desde hoy. Podés editar o eliminar una observación individual desde el tablero diario.</p>
          {loading ? <p role="status">Cargando programaciones…</p> : !list.length ? <p>No hay actividades programadas en este depósito.</p> : list.map((item) => {
            const ended = item.hasta < operationalToday();
            return <article key={item._id}>
              <h3>{item.observacion}</h3>
              <p>{item.ambito === "fiscal" ? "Fiscal" : "Nacional"} · {displayWeekdays(item.diasSemana)}</p>
              <p>{displayDate(item.desde)} al {displayDate(item.hasta)} · {item.cantidadFechas} fechas</p>
              <div className="activity-schedule-actions"><span>{item.estado === "cancelando" ? "Cancelación incompleta" : item.estado === "cancelada" ? "Cancelada" : item.estado === "preparando"
                ? "Guardado incompleto" : ended ? "Finalizada" : "Activa"}</span>
                {item.estado === "preparando" && <button type="button" disabled={busy}
                  onClick={() => mutate(() => api.post(`${endpoint}/${item._id}/reintentar${scope}`, {}), "Programación completada.")}>Reintentar guardado</button>}
                {item.estado !== "cancelada" && (!ended || item.estado === "cancelando") && <button type="button" disabled={busy} onClick={() => cancel(item)}>
                  {item.estado === "cancelando" ? "Reintentar cancelación" : "Cancelar repeticiones"}</button>}
              </div>
            </article>;
          })}
        </div>}
      </section>
    </div>, document.body)}
  </>;
}
